"use client";

import { ApiError, api, tryRefresh } from "@/lib/api";
import { authTokens } from "@/lib/auth-tokens";
import { releasePushOnLogout } from "@/lib/push-api";
import type { Role } from "@/lib/types";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import posthog from "posthog-js";

export type Me = {
  user: {
    id: string;
    name: string;
    email: string;
    avatarUrl: string | null;
    // Date de confirmation de l'adresse (premier lien magique consommé) et date
    // de création du compte — affichées dans Profil › Sécurité.
    emailVerifiedAt: string | null;
    createdAt: string;
  };
  officine: { id: string; name: string; plan: string };
  role: Role;
  // true uniquement pour l'opérateur de la plateforme (SUPER_ADMIN_EMAIL) —
  // ouvre la section /admin (instruction des demandes d'inscription).
  isSuperAdmin?: boolean;
  // Ligne effective de la matrice « Rôles & permissions » pour ce rôle
  // (voir lib/permissions.ts pour les index).
  permissions?: boolean[];
  /**
   * U-16 — second facteur. `required && !enrolled` est l'état d'un titulaire qui
   * doit encore s'enrôler : c'est le front qui l'y conduit, l'API ne bloque pas
   * la connexion d'un compte non enrôlé (elle mettrait tout le parc dehors).
   */
  totp?: { enrolled: boolean; required: boolean };
};

type Tokens = { accessToken: string };

/**
 * Réponse de `/auth/magic-link/verify` sur un compte protégé : aucune session
 * n'est délivrée, ni jeton ni cookie. Le MÊME jeton de lien doit être rejoué sur
 * `verify-totp` avec le code — c'est ce qui empêche le lien magique d'ouvrir une
 * session en contournant le second facteur.
 */
type TotpChallenge = { totpRequired: true };

type Status = "loading" | "authenticated" | "unauthenticated";

type AuthContextValue = {
  status: Status;
  me: Me | null;
  requestMagicLink: (email: string) => Promise<{ sent: boolean; magicLink?: string }>;
  /**
   * `cguAccepted` est exigé par l'API : le membre invité accepte pour lui-même,
   * le titulaire ne consent pas à sa place. Passé en paramètre plutôt que
   * codé en dur à `true` ici — une valeur toujours vraie enfouie dans le
   * provider transformerait la case en décoration.
   */
  acceptInvitation: (token: string, name: string, cguAccepted: boolean) => Promise<void>;
  /**
   * Échange un jeton de lien contre une session. Renvoie `totpRequired` quand le
   * compte est protégé : l'appelant doit alors demander le code et appeler
   * `completeTotpLogin` avec LE MÊME jeton, qui n'a pas été consommé.
   */
  loginWithToken: (token: string) => Promise<{ totpRequired: boolean }>;
  completeTotpLogin: (token: string, code: string) => Promise<void>;
  /**
   * Ouvre une session avec le code à six chiffres reçu par email, sans le lien.
   *
   * C'est la voie des appareils où le lien n'atterrit pas dans le bon
   * navigateur : sur iOS, une PWA installée et Safari ne partagent pas leurs
   * cookies, et un lien tapé dans Mail part toujours dans Safari — la session
   * s'ouvrait donc ailleurs que dans la fenêtre qui l'attendait. Un code se
   * recopie à la main, donc il traverse.
   *
   * Renvoie `totpRequired` sur un compte protégé : l'appelant redemande alors le
   * code de l'application d'authentification et rappelle avec `totpCode`. Le
   * code de connexion n'est pas consommé entre les deux, exactement comme le
   * jeton de lien ne l'est pas entre `loginWithToken` et `completeTotpLogin`.
   */
  loginWithCode: (
    email: string,
    code: string,
    totpCode?: string,
  ) => Promise<{ totpRequired: boolean }>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
  updateProfile: (name: string) => Promise<void>;
  // Pass a base64 image data URI to set the photo, or null to remove it.
  updateAvatar: (dataUri: string | null) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [me, setMe] = useState<Me | null>(null);

  async function loadMe() {
    try {
      // E13 — le jeton d'accès ne vit plus qu'en mémoire : après un rechargement
      // de page, il n'existe plus. On le rétablit depuis le cookie `HttpOnly` de
      // refresh, seul élément de session qui survit à la navigation. Sans cette
      // ligne, toute la connexion se perdrait à chaque F5.
      //
      // DANS le `try`, et pas avant : `tryRefresh()` part sur le réseau, donc
      // REJETTE quand la requête n'aboutit pas (API injoignable, Wi-Fi coupé,
      // machine qui sort de veille) — `raw()` convertit la panne de transport en
      // `ApiError(NETWORK_ERROR_STATUS)`. Laissée à l'extérieur, cette rejection
      // traversait `loadMe`, que l'effet de montage lance en `void` : personne
      // pour la rattraper. Deux conséquences, la seconde étant la vraie panne —
      //   1. le navigateur la signalait en `unhandledrejection`, d'où le bruit
      //      remonté dans Sentry (PHARMACO-WEB-3/4/5) ;
      //   2. aucun `setStatus` n'était atteint, donc `status` restait
      //      « loading » DÉFINITIVEMENT : l'application n'affichait plus que son
      //      écran d'attente, y compris sur /connexion, jusqu'au rechargement
      //      manuel.
      // Une panne réseau doit rendre la main à l'écran de connexion, pas figer
      // l'application ; le `catch` ci-dessous applique déjà exactement cette
      // règle, et prend soin de NE PAS purger le refresh token hors 401.
      if (!authTokens.access) {
        const restored = await tryRefresh();
        if (!restored) {
          setMe(null);
          setStatus("unauthenticated");
          return;
        }
      }
      const data = await api.get<Me>("/auth/me");
      setMe(data);
      setStatus("authenticated");
      posthog.identify(data.user.id, {
        email: data.user.email,
        name: data.user.name,
        role: data.role,
        officine: data.officine.name,
      });
    } catch (err) {
      // Ne détruire la session QUE sur une vraie erreur d'authentification (401,
      // déjà géré par le client API qui a purgé les jetons). Une panne serveur
      // (500) ou réseau ne doit PAS déconnecter : on garde le refresh token pour
      // qu'un simple rechargement rétablisse la session une fois l'API rétablie.
      const isAuthError = err instanceof ApiError && err.status === 401;
      if (isAuthError) {
        authTokens.clear();
      }
      setMe(null);
      setStatus("unauthenticated");
    }
  }

  useEffect(() => {
    // Legitimate mount-time session load (échange le cookie de refresh contre un
    // jeton d'accès, puis appelle /auth/me). The static rule can't see the async
    // boundary.
    /* eslint-disable-next-line react-hooks/set-state-in-effect */
    void loadMe();
  }, []);

  const value: AuthContextValue = {
    status,
    me,
    requestMagicLink: (email) =>
      api.post<{ sent: boolean; magicLink?: string }>(
        "/auth/magic-link",
        { email },
        { auth: false },
      ),
    acceptInvitation: async (token, name, cguAccepted) => {
      // Invited teammate joins the officine → creates the user + membership → logs in.
      const tokens = await api.post<Tokens>(
        "/auth/invitations/accept",
        { token, name, cguAccepted },
        { auth: false },
      );
      authTokens.set(tokens.accessToken);
      await loadMe();
      posthog.capture("invitation_accepted");
    },
    loginWithToken: async (token) => {
      const res = await api.post<Tokens | TotpChallenge>(
        "/auth/magic-link/verify",
        { token },
        { auth: false },
      );
      if ("totpRequired" in res) return { totpRequired: true };
      authTokens.set(res.accessToken);
      await loadMe();
      posthog.capture("login_completed");
      return { totpRequired: false };
    },
    completeTotpLogin: async (token, code) => {
      const tokens = await api.post<Tokens>(
        "/auth/magic-link/verify-totp",
        { token, code },
        { auth: false },
      );
      authTokens.set(tokens.accessToken);
      await loadMe();
      posthog.capture("login_completed", { second_factor: true });
    },
    loginWithCode: async (email, code, totpCode) => {
      const res = await api.post<Tokens | TotpChallenge>(
        "/auth/magic-link/verify-code",
        // `totpCode` n'est envoyé que s'il existe : un champ présent et vide
        // serait refusé par le DTO (400) au lieu de déclencher la demande de
        // second facteur (200 + `totpRequired`).
        { email, code, ...(totpCode ? { totpCode } : {}) },
        { auth: false },
      );
      if ("totpRequired" in res) return { totpRequired: true };
      authTokens.set(res.accessToken);
      await loadMe();
      // `channel` distingue les deux voies dans le suivi : c'est la mesure qui
      // dira si le code sert vraiment, et à qui.
      posthog.capture("login_completed", {
        channel: "code",
        second_factor: Boolean(totpCode),
      });
      return { totpRequired: false };
    },
    logout: async () => {
      // M19 — on rend l'abonnement push AVANT la session, et pas seulement par
      // symétrie : la suppression côté serveur a besoin du jeton d'accès, qui
      // n'existe plus après `authTokens.clear()`. C'est aussi ce qui rend le
      // poste partagé utilisable, puisque l'API refuse désormais de reprendre
      // l'endpoint d'un autre compte (voir releasePushOnLogout).
      // Best-effort, comme l'appel ci-dessous : un abonnement qu'on n'a pas su
      // rendre ne doit pas retenir quelqu'un devant un écran encore connecté.
      try {
        await releasePushOnLogout();
      } catch {
        // ignore — la déconnexion locale prime
      }
      // Plus de jeton à transmettre : le cookie `HttpOnly` suffit à l'API, qui
      // l'efface au passage (E13). L'appel reste best-effort — une coupure
      // réseau ne doit pas empêcher de se déconnecter localement.
      try {
        await api.post("/auth/logout", {}, { auth: false });
      } catch {
        // ignore — we clear locally regardless
      }
      posthog.reset();
      authTokens.clear();
      setMe(null);
      setStatus("unauthenticated");
    },
    refreshMe: loadMe,
    updateProfile: async (name: string) => {
      const data = await api.patch<Me>("/auth/me", { name });
      setMe(data);
    },
    updateAvatar: async (dataUri: string | null) => {
      // "" tells the API to clear the stored photo.
      const data = await api.patch<Me>("/auth/me", { avatarDataUri: dataUri ?? "" });
      setMe(data);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
